import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { X } from "lucide-react";

const menuItems = [
  { label: "Accueil", href: "/" },
  { label: "Nos services", href: "/services" },
  { label: "Notre équipe", href: "/equipe" },
  { label: "Contact", href: "/contact" }
];

export default function Header() {
  const location = useLocation();
  const [hovered, setHovered] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [isOverWhiteSection, setIsOverWhiteSection] = useState(false);
  const refs = useRef([]);
  const [offsets, setOffsets] = useState([]);
  const [widths, setWidths] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Determine current page index based on location
  const getCurrentIndex = () => {
    if (location.pathname === "/") return 0;
    if (location.pathname === "/services") return 1;
    if (location.pathname === "/equipe") return 2;
    if (location.pathname === "/contact") return 3;
    return 0;
  };

  const current = getCurrentIndex();

  useEffect(() => {
    const updateOffsets = () => {
      const newOffsets = refs.current.map((el) => el?.offsetLeft ?? 0);
      const newWidths = refs.current.map((el) => el?.offsetWidth ?? 0);
      setOffsets(newOffsets);
      setWidths(newWidths);
    };

    updateOffsets();
    window.addEventListener('resize', updateOffsets);
    
    return () => window.removeEventListener('resize', updateOffsets);
  }, []);

  // Add scroll listener for background and section detection
  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      const viewportHeight = window.innerHeight;
      
      // Check if scrolled
      setScrolled(offset > 10);
      
      // Detect if over white/light section
      // On homepage, check if past the fullscreen slider
      if (location.pathname === "/") {
        setIsOverWhiteSection(offset > viewportHeight * 0.8);
      } 
      // On services/contact pages, check if past hero section (typically 70vh)
      else if (location.pathname === "/services" || location.pathname === "/contact") {
        setIsOverWhiteSection(offset > viewportHeight * 0.6);
      } 
      // Default behavior for other pages
      else {
        setIsOverWhiteSection(offset > 10);
      }
    };

    handleScroll(); // Initial check
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  // Determine if header should use dark mode (white background)
  const useDarkMode = scrolled && isOverWhiteSection;

  return (
    <>
    <header 
      className={`w-full fixed top-0 left-0 z-50 font-sans transition-all duration-300 ${
        useDarkMode 
          ? 'bg-white shadow-md text-[#0B2545]' 
          : 'bg-transparent text-white'
      }`}
    >
      {/* Top Bar */}
      <div className={`w-full bg-transparent text-xs tracking-wide flex justify-between items-center px-6 py-2 font-light transition-colors duration-300 ${
        useDarkMode ? 'text-[#666]' : 'text-white'
      }`}>
        <div className="flex gap-6">
          <a href="https://maps.app.goo.gl/qtpEDc4tsV6HRtj56" className="hover:underline">
            93 Rue de La Palestine Beb El Bhar, Tunis 1002
          </a>
          <a href="tel:+216 98 222 635 - 22 515 074"  className="hover:underline">
            +216 98 222 635 - 22 515 074
          </a>
        </div>
        <div className="flex gap-4 items-center"></div>
      </div>

      {/* Thin Line */}
      <div className={`h-[1px] w-full transition-colors duration-300 ${
        useDarkMode ? 'bg-gray-200' : 'bg-white/10'
      }`} />

      {/* Nav */}
      <div className="flex justify-between items-center px-10 py-4">
        {/* Logo */}
        <Link to="/" className="text-[1.5rem] font-semibold tracking-widest ml-6">
          Bankelys
        </Link>

        {/* Navigation */}
        <nav
          className="relative hidden md:flex gap-8 text-sm uppercase tracking-wider font-medium"
          onMouseLeave={() => setHovered(null)}
        >
          {menuItems.map((item, i) => (
            <div
              key={item.label}
              className="relative"
              ref={(el) => (refs.current[i] = el)}
              onMouseEnter={() => setHovered(i)}
            >
              <Link
                to={item.href}
                className={`cursor-pointer relative transition-colors ${
                  useDarkMode
                    ? current === i 
                      ? 'text-[#0B2545] hover:text-[#0B2545]/70' 
                      : 'text-[#0B2545]/70 hover:text-[#0B2545]'
                    : current === i 
                      ? 'text-white hover:text-gray-300' 
                      : 'text-white/80 hover:text-white'
                }`}
              >
                {item.label}
              </Link>
            </div>
          ))}

          {/* Hover underline */}
          {hovered !== null && (
            <motion.span
              className={`absolute bottom-0 h-[2px] transition-colors duration-300 ${
                useDarkMode ? 'bg-[#0B2545]' : 'bg-white'
              }`}
              initial={false}
              animate={{
                left: offsets[hovered],
                width: widths[hovered],
              }}
              transition={{ type: "tween", duration: 0.3 }}
            />
          )}

          {/* Current page top line */}
          {offsets.length > 0 && current !== null && (
            <motion.span
              className={`absolute -top-4 h-[2px] transition-colors duration-300 ${
                useDarkMode ? 'bg-[#0B2545]' : 'bg-white'
              }`}
              initial={false}
              animate={{
                left: offsets[current],
                width: widths[current],
              }}
              transition={{ type: "tween", duration: 0.4 }}
            />
          )}
        </nav>

        {/* Right Icons */}
        <div className="flex gap-6 items-center">
          {/* Side Area Opener (dots) */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden transition-colors duration-300 ${
              useDarkMode ? 'hover:text-[#0B2545]/70' : 'hover:text-gray-300'
            }`}
            aria-label="Toggle menu"
          >
            <svg className="w-5 h-5" viewBox="0 0 22 22" fill={useDarkMode ? "#0B2545" : "white"}>
              {[2, 11, 20].flatMap((y) =>
                [2, 11, 20].map((x, i) => (
                  <circle key={`${x}-${y}-${i}`} cx={x} cy={y} r="2" />
                ))
              )}
            </svg>
          </button>
        </div>
      </div>
    </header>
     <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] md:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />

            {/* Menu Panel */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3 }}
              className="fixed top-0 right-0 h-full w-[80%] max-w-sm bg-white shadow-2xl z-[70] md:hidden"
            >
              {/* Close Button */}
              <div className="flex justify-between items-center p-6 border-b border-gray-200">
                <span className="text-xl font-semibold text-[#0B2545]">Menu</span>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
                  aria-label="Close menu"
                >
                  <X className="w-6 h-6 text-[#0B2545]" />
                </button>
              </div>

              {/* Mobile Navigation Links */}
              <nav className="flex flex-col p-6">
                {menuItems.map((item, i) => (
                  <Link
                    key={item.label}
                    to={item.href}
                    className={`py-4 text-lg font-medium transition-colors border-b border-gray-100 ${
                      current === i
                        ? 'text-[#0B2545] font-semibold'
                        : 'text-[#0B2545]/70 hover:text-[#0B2545]'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}

                {/* Contact Info */}
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Adresse</p>
                      <a 
                        href="https://maps.app.goo.gl/qtpEDc4tsV6HRtj56" 
                        className="text-sm text-[#0B2545] hover:underline"
                      >
                        93 Rue de La Palestine<br />
                        Beb El Bhar, Tunis 1002
                      </a>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Email</p>
                      <a 
                        href="mailto:contact@bankelys.com" 
                        className="text-sm text-[#0B2545] hover:underline"
                      >
                        contact@bankelys.com
                      </a>
                    </div>
                  </div>
                </div>
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      </>
  );
}